import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MdPerson,
  MdSearch,
  MdRefresh,
  MdDownload,
  MdVisibility,
  MdEdit,
  MdDelete,
  MdAdd,
  MdEmail,
  MdPhone,
  MdLocationOn,
  MdWork,
  MdCalendarToday,
  MdSecurity,
  MdInfo,
  MdClose,
  MdCheckCircle,
  MdFlag,
  MdGroup
} from 'react-icons/md';
import UsibrasLoader from '../../../components/UsibrasLoader';
import UsibrasToast from '../../../components/UsibrasToast';
import { tokenManager } from '../../../utils/tokenManager';

const WorkerList = () => {
  const navigate = useNavigate();
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTable, setFilterTable] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [workers, setWorkers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, per_page: 50, total_pages: 1, total_records: 0 });

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editErrors, setEditErrors] = useState({});

  // Workers data from API (server-side pagination)
  const fetchWorkers = async (page = 1, showSuccessToast = false) => {
    setIsLoading(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        await new Promise(resolve => setTimeout(resolve, 300));
        const retryAuthHeader = tokenManager.getAuthHeader();
        if (!retryAuthHeader) {
          throw new Error('Authentication token not found. Please login again.');
        }
        await fetchWorkersWithToken(retryAuthHeader, page, showSuccessToast);
      }
      await fetchWorkersWithToken(authHeader, page, showSuccessToast);
    } catch (error) {
      showToast('Error fetching workers: ' + error.message, 'error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function with token
  const fetchWorkersWithToken = async (authHeader, page, showSuccessToast) => {
    try {
      const resp = await fetch(`https://usibras-web-backend.onrender.com/api/manual-peeling-workers?page=${page}`, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
      });

      const result = await resp.json();

      if (resp.ok) {
        const mapped = (result.data || []).map((worker) => ({
          id: worker.id,
          cardNumber: worker.card_number,
          firstName: worker.first_name,
          lastName: worker.last_name,
          email: worker.drive_email || worker.email || '',
          phone: worker.phone,
          address: worker.address || '',
          dateOfBirth: worker.date_of_birth,
          gender: worker.gender || '',
          department: 'Manual Peeling',
          position: 'Peeling Worker',
          startDate: worker.start_date,
          workType: worker.work_type,
          shift: worker.shift,
          tableNumber: worker.table_number,
          experience: worker.experience || '',
          skills: worker.skills || '',
          notes: worker.notes || '',
          dailyTarget: parseFloat(worker.daily_target),
          emergencyContactName: worker.emergency_contact_name || '',
          emergencyContactPhone: worker.emergency_contact_phone || '',
          emergencyContactRelationship: worker.emergency_contact_relationship || '',
          status: worker.status || 'Active',
        }));

        setWorkers(mapped);
        const p = result.pagination || {};
        setPagination({
          page: p.page || page,
          per_page: p.per_page || (p.page_size || 50),
          total_pages: p.total_pages || 1,
          total_records: p.total_records || (Array.isArray(result.data) ? result.data.length : 0),
        });

        if (showSuccessToast) {
          showToast(result.message || 'Workers refreshed successfully', 'success');
        }
      } else {
        showToast(result.message || result.error || 'Failed to load workers', 'error');
      }
    } catch (e) {
      throw e;
    }
  };

  // initial + on page change
  useEffect(() => {
    // small delay to ensure tokens are available
    const timer = setTimeout(() => {
      fetchWorkers(currentPage, false);
    }, 150);
    return () => clearTimeout(timer);
  }, [currentPage]);

  // Reset to first page when filters/search change
  useEffect(() => {
    setCurrentPage(0);
    // queue to next tick to trigger fetch with page 1
    const t = setTimeout(() => setCurrentPage(1), 0);
    return () => clearTimeout(t);
  }, [searchQuery, filterTable, filterStatus]);

  // Filter workers (on current page data)
  const getFilteredWorkers = () => {
    return (workers || []).filter((worker) => {
      const queryLc = (searchQuery || '').toLowerCase();
      const firstNameLc = (worker.firstName || '').toLowerCase();
      const lastNameLc = (worker.lastName || '').toLowerCase();
      const cardNumberLc = (worker.cardNumber || '').toLowerCase();
      const phoneStr = (worker.phone || '').toString();

      const matchesSearch =
        firstNameLc.includes(queryLc) ||
        lastNameLc.includes(queryLc) ||
        cardNumberLc.includes(queryLc) ||
        phoneStr.includes(searchQuery || '');

      const matchesTable = filterTable === 'all' || (worker.tableNumber || '').toString() === filterTable.toString();
      const matchesStatus = filterStatus === 'all' || (worker.status || '').toLowerCase() === (filterStatus || '').toLowerCase();

      return matchesSearch && matchesTable && matchesStatus;
    });
  };

  const filteredWorkers = getFilteredWorkers();
  const totalPages = pagination.total_pages || 1;
  const pageStart = filteredWorkers.length > 0 ? (pagination.page - 1) * (pagination.per_page || filteredWorkers.length) + 1 : 0;
  const pageEnd = (pagination.page - 1) * (pagination.per_page || 0) + filteredWorkers.length;

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000);
  };

  // Handle view worker details
  const handleViewWorker = (worker) => {
    setSelectedWorker(worker);
    setShowDetailModal(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedWorker(null);
  };

  // Handle edit worker
  const handleEditWorker = (worker) => {
    setSelectedWorker(worker);
    setEditFormData({
      first_name: worker.firstName,
      last_name: worker.lastName,
      email: worker.email || '',
      phone: worker.phone,
      address: worker.address || '',
      date_of_birth: worker.dateOfBirth,
      gender: worker.gender || '',
      card_number: worker.cardNumber,
      start_date: worker.startDate,
      work_type: worker.workType,
      shift: worker.shift,
      table_number: worker.tableNumber,
      experience: worker.experience || '',
      daily_target: worker.dailyTarget,
      emergency_contact_name: worker.emergencyContactName || '',
      emergency_contact_phone: worker.emergencyContactPhone || '',
      emergency_contact_relationship: worker.emergencyContactRelationship || '',
      skills: worker.skills || '',
      notes: worker.notes || ''
    });
    setEditErrors({});
    setShowEditModal(true);
  };

  // Handle close edit modal
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedWorker(null);
    setEditFormData({});
    setEditErrors({});
  };

  // Handle edit form change
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (editErrors[name]) {
      setEditErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate edit form
  const validateEditForm = () => {
    const errors = {};
    if (!editFormData.first_name?.trim()) errors.first_name = 'First name is required';
    if (!editFormData.last_name?.trim()) errors.last_name = 'Last name is required';
    if (!editFormData.phone?.trim()) errors.phone = 'Phone is required';
    if (!editFormData.card_number?.trim()) errors.card_number = 'Card number is required';
    if (!editFormData.date_of_birth) errors.date_of_birth = 'Date of birth is required';
    if (!editFormData.start_date) errors.start_date = 'Start date is required';
    if (!editFormData.work_type) errors.work_type = 'Work type is required';
    if (!editFormData.shift) errors.shift = 'Shift is required';
    if (!editFormData.table_number) errors.table_number = 'Table number is required';
    if (!editFormData.daily_target || parseFloat(editFormData.daily_target) <= 0) {
      errors.daily_target = 'Valid daily target is required';
    }
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle submit edit
  const handleSubmitEdit = async () => {
    if (!validateEditForm()) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const response = await fetch(`https://usibras-web-backend.onrender.com/api/manual-peeling-workers/${selectedWorker.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editFormData)
      });

      const result = await response.json();

      if (response.ok) {
        showToast(result.message || 'Worker updated successfully', 'success');
        handleCloseEditModal();
        // Refresh the worker list
        fetchWorkers(currentPage, false);
      } else {
        showToast(result.message || result.error || 'Failed to update worker', 'error');
      }
    } catch (error) {
      showToast('Error updating worker: ' + error.message, 'error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete worker - open modal
  const handleDeleteWorker = (worker) => {
    setSelectedWorker(worker);
    setShowDeleteModal(true);
  };

  // Handle close delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedWorker(null);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!selectedWorker) return;

    setIsLoading(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const response = await fetch(`https://usibras-web-backend.onrender.com/api/manual-peeling-workers/${selectedWorker.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (response.ok) {
        showToast(result.message || 'Worker deleted successfully', 'success');
        handleCloseDeleteModal();
        // Refresh the worker list
        fetchWorkers(currentPage, false);
      } else {
        showToast(result.message || result.error || 'Failed to delete worker', 'error');
      }
    } catch (error) {
      showToast('Error deleting worker: ' + error.message, 'error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchWorkers(currentPage, true); // Show success toast on manual refresh
  };

  // Handle export filtered workers to CSV
  const handleExport = () => {
    const data = getFilteredWorkers();
    if (data.length === 0) {
      showToast('No workers to export', 'info');
      return;
    }

    const headers = [
      'Card Number', 'First Name', 'Last Name', 'Email', 'Phone', 'Address',
      'Date of Birth', 'Gender', 'Department', 'Position', 'Start Date',
      'Work Type', 'Shift', 'Table', 'Daily Target (kg)', 'Experience', 'Skills',
      'Notes', 'Status', 'Emergency Contact', 'Emergency Phone', 'Relationship'
    ];

    const escapeCsv = (val) => {
      if (val == null || val === '') return '';
      const s = String(val);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const rows = data.map((w) => [
      escapeCsv(w.cardNumber),
      escapeCsv(w.firstName),
      escapeCsv(w.lastName),
      escapeCsv(w.email),
      escapeCsv(w.phone),
      escapeCsv(w.address),
      escapeCsv(w.dateOfBirth),
      escapeCsv(w.gender),
      escapeCsv(w.department),
      escapeCsv(w.position),
      escapeCsv(w.startDate),
      escapeCsv(w.workType),
      escapeCsv(w.shift),
      escapeCsv(w.tableNumber),
      escapeCsv(w.dailyTarget != null ? Number(w.dailyTarget) : ''),
      escapeCsv(w.experience),
      escapeCsv(w.skills),
      escapeCsv(w.notes),
      escapeCsv(w.status),
      escapeCsv(w.emergencyContactName),
      escapeCsv(w.emergencyContactPhone),
      escapeCsv(w.emergencyContactRelationship),
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `manual-peeling-workers-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    showToast(`${data.length} worker(s) exported successfully`, 'success');
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center">
            <MdPerson className="mr-3 text-blue-600" />
            Worker List
          </h1>
        </div>
        <p className="text-gray-600">
          Manage and view all manual peeling workers
        </p>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MdSearch className="inline mr-2 text-blue-500" />
              Search Workers
            </label>
            <div className="relative">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, card number, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <MdClose />
                </button>
              )}
            </div>
          </div>

          {/* Filter by Table */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MdGroup className="inline mr-2 text-purple-500" />
              Filter by Table
            </label>
            <select
              value={filterTable}
              onChange={(e) => setFilterTable(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">All Tables</option>
              <option value="AP">AP</option>
              {Array.from({ length: 10 }, (_, i) => (
                <option key={i + 1} value={`${i + 1}`}>{i + 1}</option>
              ))}
            </select>
          </div>

          {/* Filter by Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MdCheckCircle className="inline mr-2 text-green-500" />
              Filter by Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">All Status</option>
              <option value="current">Current</option>
              <option value="new">New</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="on leave">On Leave</option>
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            {isLoading
              ? 'Loading workers...'
              : `Showing ${pageStart} to ${pageEnd} of ${pagination.total_records} worker${pagination.total_records !== 1 ? 's' : ''}`}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/admin/manualpeeling/add-worker')}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center font-medium"
            >
              <MdAdd className="mr-2" />
              Add Worker
            </button>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center font-medium"
            >
              <MdRefresh className="mr-2" />
              Refresh
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center font-medium"
            >
              <MdDownload className="mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Workers Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                  Card Number
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                  Table
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                  Daily Target
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                  Start Date
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <UsibrasLoader />
                    </div>
                  </td>
                </tr>
              ) : filteredWorkers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <MdSearch className="text-6xl text-gray-300 mb-4" />
                      <p className="text-lg font-medium text-gray-600 mb-2">No workers found</p>
                      <p className="text-sm text-gray-500">
                        {searchQuery ? `No workers match "${searchQuery}"` : 'No workers available'}
                      </p>
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          Clear Search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredWorkers.map((worker) => (
                  <tr key={worker.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{worker.cardNumber}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <MdPerson className="text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {worker.firstName} {worker.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{worker.email || 'No email'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {worker.phone}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-3 py-1 text-xs font-semibold bg-purple-100 text-purple-800 rounded-full">
                        {worker.tableNumber}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-3 py-1 text-sm font-semibold bg-green-100 text-green-800 rounded-lg">
                        {worker.dailyTarget.toFixed(1)} kg
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(worker.startDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        worker.status === 'Active' || worker.status === 'Current' ? 'bg-green-100 text-green-800' :
                        worker.status === 'New' ? 'bg-blue-100 text-blue-800' :
                        worker.status === 'Inactive' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {worker.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewWorker(worker)}
                          className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-1"
                          title="View Details"
                        >
                          <MdVisibility className="text-lg" />
                          <span>View</span>
                        </button>
                        <button
                          onClick={() => handleEditWorker(worker)}
                          className="px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                          title="Edit Worker"
                        >
                          <MdEdit className="text-lg" />
                        </button>
                        <button
                          onClick={() => handleDeleteWorker(worker)}
                          className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                          title="Delete Worker"
                        >
                          <MdDelete className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {pageStart} to {pageEnd} of {pagination.total_records} workers
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={isLoading || currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-blue-500 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                      disabled={isLoading}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={isLoading || currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Worker Detail Modal */}
      {showDetailModal && selectedWorker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <MdPerson className="text-3xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">
                      {selectedWorker.firstName} {selectedWorker.lastName}
                    </h3>
                    <p className="text-blue-100 mt-1">{selectedWorker.cardNumber}</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <MdClose className="text-2xl" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Personal Information */}
              <div className="mb-6">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg mb-4">
                  <h4 className="text-lg font-semibold text-blue-800 flex items-center">
                    <MdPerson className="mr-2" />
                    Personal Information
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">First Name</label>
                    <p className="text-base font-semibold text-gray-900">{selectedWorker.firstName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Last Name</label>
                    <p className="text-base font-semibold text-gray-900">{selectedWorker.lastName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Date of Birth</label>
                    <p className="text-base font-semibold text-gray-900">
                      {new Date(selectedWorker.dateOfBirth).toLocaleDateString()} ({calculateAge(selectedWorker.dateOfBirth)} years)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Gender</label>
                    <p className="text-base font-semibold text-gray-900">{selectedWorker.gender || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                    <div className="flex items-center">
                      <MdEmail className="text-gray-400 mr-2" />
                      <p className="text-base font-semibold text-gray-900">{selectedWorker.email || 'Not provided'}</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Phone</label>
                    <div className="flex items-center">
                      <MdPhone className="text-gray-400 mr-2" />
                      <p className="text-base font-semibold text-gray-900">{selectedWorker.phone}</p>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Address</label>
                    <div className="flex items-start">
                      <MdLocationOn className="text-gray-400 mr-2 mt-1" />
                      <p className="text-base font-semibold text-gray-900">{selectedWorker.address || 'Not provided'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Work Information */}
              <div className="mb-6">
                <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg mb-4">
                  <h4 className="text-lg font-semibold text-green-800 flex items-center">
                    <MdWork className="mr-2" />
                    Work Information
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Department</label>
                    <p className="text-base font-semibold text-gray-900">{selectedWorker.department}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Position</label>
                    <p className="text-base font-semibold text-gray-900">{selectedWorker.position}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Start Date</label>
                    <div className="flex items-center">
                      <MdCalendarToday className="text-gray-400 mr-2" />
                      <p className="text-base font-semibold text-gray-900">
                        {new Date(selectedWorker.startDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Work Type</label>
                    <p className="text-base font-semibold text-gray-900">{selectedWorker.workType}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Shift</label>
                    <p className="text-base font-semibold text-gray-900">{selectedWorker.shift}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Table Number</label>
                    <span className="inline-flex px-3 py-1 text-sm font-semibold bg-purple-100 text-purple-800 rounded-full">
                      {selectedWorker.tableNumber}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Experience</label>
                    <p className="text-base font-semibold text-gray-900">{selectedWorker.experience || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                      selectedWorker.status === 'Active' || selectedWorker.status === 'Current' ? 'bg-green-100 text-green-800' :
                      selectedWorker.status === 'New' ? 'bg-blue-100 text-blue-800' :
                      selectedWorker.status === 'Inactive' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedWorker.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Target Information */}
              <div className="mb-6">
                <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg mb-4">
                  <h4 className="text-lg font-semibold text-green-800 flex items-center">
                    <MdFlag className="mr-2" />
                    Target Information
                  </h4>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="text-center">
                    <p className="text-sm font-medium text-green-600 mb-2">Daily Target</p>
                    <p className="text-4xl font-bold text-green-700">
                      {selectedWorker.dailyTarget.toFixed(1)} <span className="text-2xl">kg</span>
                    </p>
                    <p className="text-xs text-green-600 mt-2">Daily Output Goal</p>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="mb-6">
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg mb-4">
                  <h4 className="text-lg font-semibold text-orange-800 flex items-center">
                    <MdSecurity className="mr-2" />
                    Emergency Contact
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Contact Name</label>
                    <p className="text-base font-semibold text-gray-900">{selectedWorker.emergencyContactName || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Contact Phone</label>
                    <div className="flex items-center">
                      <MdPhone className="text-gray-400 mr-2" />
                      <p className="text-base font-semibold text-gray-900">{selectedWorker.emergencyContactPhone || 'Not provided'}</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Relationship</label>
                    <p className="text-base font-semibold text-gray-900">{selectedWorker.emergencyContactRelationship || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg mb-4">
                  <h4 className="text-lg font-semibold text-purple-800 flex items-center">
                    <MdInfo className="mr-2" />
                    Additional Information
                  </h4>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Skills</label>
                    <p className="text-base text-gray-900 bg-gray-50 p-3 rounded-lg">
                      {selectedWorker.skills || 'No skills listed'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Notes</label>
                    <p className="text-base text-gray-900 bg-gray-50 p-3 rounded-lg">
                      {selectedWorker.notes || 'No notes available'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleCloseModal}
                  className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleCloseModal();
                    handleEditWorker(selectedWorker);
                  }}
                  className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium flex items-center"
                >
                  <MdEdit className="mr-2" />
                  Edit Worker
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Worker Modal */}
      {showEditModal && selectedWorker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full my-8 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <MdEdit className="text-2xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Edit Worker</h3>
                    <p className="text-yellow-100 mt-1">{selectedWorker.cardNumber} - {selectedWorker.firstName} {selectedWorker.lastName}</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseEditModal}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <MdClose className="text-2xl" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <form className="space-y-6">
                {/* Personal Information Section */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                    Personal Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={editFormData.first_name || ''}
                        onChange={handleEditFormChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                          editErrors.first_name ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {editErrors.first_name && <p className="text-red-500 text-xs mt-1">{editErrors.first_name}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        value={editFormData.last_name || ''}
                        onChange={handleEditFormChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                          editErrors.last_name ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {editErrors.last_name && <p className="text-red-500 text-xs mt-1">{editErrors.last_name}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Card Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="card_number"
                        value={editFormData.card_number || ''}
                        onChange={handleEditFormChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                          editErrors.card_number ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {editErrors.card_number && <p className="text-red-500 text-xs mt-1">{editErrors.card_number}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={editFormData.phone || ''}
                        onChange={handleEditFormChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                          editErrors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {editErrors.phone && <p className="text-red-500 text-xs mt-1">{editErrors.phone}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={editFormData.email || ''}
                        onChange={handleEditFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                      <select
                        name="gender"
                        value={editFormData.gender || ''}
                        onChange={handleEditFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="date_of_birth"
                        value={editFormData.date_of_birth || ''}
                        onChange={handleEditFormChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                          editErrors.date_of_birth ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {editErrors.date_of_birth && <p className="text-red-500 text-xs mt-1">{editErrors.date_of_birth}</p>}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <input
                        type="text"
                        name="address"
                        value={editFormData.address || ''}
                        onChange={handleEditFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Work Information Section */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                    Work Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="start_date"
                        value={editFormData.start_date || ''}
                        onChange={handleEditFormChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                          editErrors.start_date ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {editErrors.start_date && <p className="text-red-500 text-xs mt-1">{editErrors.start_date}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Work Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="work_type"
                        value={editFormData.work_type || ''}
                        onChange={handleEditFormChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                          editErrors.work_type ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Work Type</option>
                        <option value="Full Time">Full Time</option>
                        <option value="Part Time">Part Time</option>
                        <option value="Contract">Contract</option>
                      </select>
                      {editErrors.work_type && <p className="text-red-500 text-xs mt-1">{editErrors.work_type}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Shift <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="shift"
                        value={editFormData.shift || ''}
                        onChange={handleEditFormChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                          editErrors.shift ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Shift</option>
                        <option value="Day Shift">Day Shift</option>
                        <option value="Night Shift">Night Shift</option>
                        <option value="Rotating">Rotating</option>
                      </select>
                      {editErrors.shift && <p className="text-red-500 text-xs mt-1">{editErrors.shift}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Table Number <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="table_number"
                        value={editFormData.table_number || ''}
                        onChange={handleEditFormChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                          editErrors.table_number ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Table</option>
                        <option value="AP">AP</option>
                        {Array.from({ length: 10 }, (_, i) => (
                          <option key={i + 1} value={`${i + 1}`}>{i + 1}</option>
                        ))}
                      </select>
                      {editErrors.table_number && <p className="text-red-500 text-xs mt-1">{editErrors.table_number}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Daily Target (kg) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        name="daily_target"
                        value={editFormData.daily_target || ''}
                        onChange={handleEditFormChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                          editErrors.daily_target ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {editErrors.daily_target && <p className="text-red-500 text-xs mt-1">{editErrors.daily_target}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Experience</label>
                      <input
                        type="text"
                        name="experience"
                        value={editFormData.experience || ''}
                        onChange={handleEditFormChange}
                        placeholder="e.g., 5 years"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Emergency Contact Section */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                    Emergency Contact
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                      <input
                        type="text"
                        name="emergency_contact_name"
                        value={editFormData.emergency_contact_name || ''}
                        onChange={handleEditFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                      <input
                        type="tel"
                        name="emergency_contact_phone"
                        value={editFormData.emergency_contact_phone || ''}
                        onChange={handleEditFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                      <input
                        type="text"
                        name="emergency_contact_relationship"
                        value={editFormData.emergency_contact_relationship || ''}
                        onChange={handleEditFormChange}
                        placeholder="e.g., Spouse, Sibling"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Information Section */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                    Additional Information
                  </h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Skills</label>
                      <textarea
                        name="skills"
                        value={editFormData.skills || ''}
                        onChange={handleEditFormChange}
                        rows="2"
                        placeholder="e.g., Fast peeling, Quality inspection"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        name="notes"
                        value={editFormData.notes || ''}
                        onChange={handleEditFormChange}
                        rows="3"
                        placeholder="Additional notes about the worker"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleCloseEditModal}
                  className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitEdit}
                  className="px-6 py-2.5 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-medium flex items-center"
                  disabled={isLoading}
                >
                  <MdEdit className="mr-2" />
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedWorker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <MdDelete className="text-2xl text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Delete Worker</h3>
                  <p className="text-red-100 text-sm mt-1">This action cannot be undone</p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  Are you sure you want to delete this worker?
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-3">
                      <MdPerson className="text-red-600 text-xl" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {selectedWorker.firstName} {selectedWorker.lastName}
                      </p>
                      <p className="text-sm text-gray-600">Card: {selectedWorker.cardNumber}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Phone:</span>
                      <p className="font-medium text-gray-900">{selectedWorker.phone}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Table:</span>
                      <p className="font-medium text-gray-900">{selectedWorker.tableNumber}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-red-800">Warning</p>
                    <p className="text-xs text-red-700 mt-1">
                      Deleting this worker will permanently remove all their records and cannot be recovered.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleCloseDeleteModal}
                  className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium flex items-center"
                  disabled={isLoading}
                >
                  <MdDelete className="mr-2" />
                  {isLoading ? 'Deleting...' : 'Delete Worker'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && <UsibrasLoader />}

      {/* Toast Notifications */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'info' })}
        />
      )}
    </div>
  );
};

export default WorkerList;

